import api from '@shared/services/api';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Comment, CommentResponse, CreatePostData, Post, PostResponse } from '../types/post.types';

interface PostsResponse {
  posts: Post[];
  total: number;
  currentPage: number;
  hasNextPage: boolean;
}

interface PostStore {
  // State
  posts: Post[];
  currentPost: Post | null;
  loading: boolean;
  error: string | null;
  currentPage: number;
  hasNextPage: boolean;
  total: number;
  isDeletingComment: boolean;
  isDeleting: boolean;

  // Post Actions
  fetchPosts: (page?: number) => Promise<void>;
  getPost: (postId: string) => Promise<Post>;
  createPost: (data: CreatePostData) => Promise<Post>;
  likePost: (postId: string) => Promise<void>;
  unlikePost: (postId: string) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
  sharePost: (postId: string, content?: string) => Promise<void>;

  // Comment Actions
  addComment: (postId: string, content: string) => Promise<Comment>;
  deleteComment: (postId: string, commentId: string) => Promise<void>;
  getComments: (postId: string, page: number, limit?: number) => Promise<any>;

  // Store Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const ITEMS_PER_PAGE = 10;
const COMMENTS_PER_PAGE = 3;

export const usePostStore = create<PostStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      posts: [],
      currentPost: null,
      loading: false,
      error: null,
      currentPage: 1,
      hasNextPage: false,
      total: 0,
      isDeletingComment: false,
      isDeleting: false,

      // Post Actions
      getPost: async (postId: string) => {
        try {
          set({ loading: true, error: null });
          const response = await api.get<Post>(`/posts/${postId}`);
          const post = response.data;
          set({ currentPost: post, loading: false });
          return post;
        } catch (error) {
          set({ error: 'Error fetching post', loading: false, currentPost: null });
          throw error;
        }
      },

      fetchPosts: async (page = 1) => {
        try {
          set({ loading: true, error: null });
          const response = await api.get<PostsResponse>(
            `/posts/feed?page=${page}&limit=${ITEMS_PER_PAGE}`
          );

          if (page === 1) {
            set({ posts: response.data.posts });
          } else {
            set((state) => ({
              posts: [...state.posts, ...response.data.posts],
            }));
          }

          set({
            currentPage: response.data.currentPage,
            hasNextPage: response.data.hasNextPage,
            total: response.data.total,
          });
        } catch (error) {
          set({ error: 'Error fetching posts' });
        } finally {
          set({ loading: false });
        }
      },

      createPost: async (data: CreatePostData) => {
        try {
          set({ loading: true, error: null });
          const response = await api.post<PostResponse>('/posts', data);
          const newPost = response.data.post;
          set((state) => ({
            posts: [newPost, ...state.posts],
            total: state.total + 1,
          }));
          return newPost;
        } catch (error) {
          set({ error: 'Error creating post' });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      likePost: async (postId: string) => {
        try {
          await api.post(`/posts/${postId}/like`);
          set((state) => {
            const updatedPosts = state.posts.map((post) =>
              post.id === postId
                ? { ...post, likes: post.likes + 1, isLiked: true }
                : post
            );
            
            return {
              posts: updatedPosts,
              currentPost: state.currentPost?.id === postId
                ? { ...state.currentPost, likes: state.currentPost.likes + 1, isLiked: true }
                : state.currentPost
            };
          });
        } catch (error) {
          set({ error: 'Error liking post' });
          throw error;
        }
      },

      unlikePost: async (postId: string) => {
        try {
          await api.post(`/posts/${postId}/like`);
          set((state) => {
            const updatedPosts = state.posts.map((post) =>
              post.id === postId
                ? { ...post, likes: post.likes - 1, isLiked: false }
                : post
            );
            
            return {
              posts: updatedPosts,
              currentPost: state.currentPost?.id === postId
                ? { ...state.currentPost, likes: state.currentPost.likes - 1, isLiked: false }
                : state.currentPost
            };
          });
        } catch (error) {
          set({ error: 'Error unliking post' });
          throw error;
        }
      },

      deletePost: async (postId: string) => {
        set({ isDeleting: true });
        try {
          await api.delete(`/posts/${postId}`);
          set((state) => ({
            posts: state.posts.filter((post) => post.id !== postId),
            total: state.total - 1,
          }));
        } catch (error) {
          set({ error: 'Error deleting post' });
          throw error;
        } finally {
          set({ isDeleting: false });
        }
      },

      // Comment Actions
      addComment: async (postId: string, content: string) => {
        try {
          const response = await api.post<CommentResponse>(`/posts/${postId}/comments`, { content });
          const newComment = response.data.comment;
          if (!newComment) {
            throw new Error('Error adding comment');
          }

          set((state) => {
            const comments = (list?: Comment[]) => 
              list?.length >= 3 ? list.slice(0, -1) : list || [];

            const updatedPosts = state.posts.map((post) => 
              post.id === postId
                ? {
                  ...post,
                  comments: post.comments + 1,
                  commentList: [newComment, ...comments(post.commentList)],
                }
                : post
            );
            
            return {
              posts: updatedPosts,
              currentPost: state.currentPost?.id === postId
                ? {
                  ...state.currentPost,
                  comments: state.currentPost.comments + 1,
                  commentList: [newComment, ...comments(state.currentPost.commentList)],
                }
                : state.currentPost
            };
          })
          return newComment;
        } catch (error) {
          set({ error: 'Error adding comment' });
          throw error;
        }
      },

      deleteComment: async (postId: string, commentId: string) => {
        set({ isDeletingComment: true });
        try {
          await api.delete(`/posts/${postId}/comments/${commentId}`);

          // Get fresh post data
          const postResponse = await api.get(`/posts/${postId}`);
          const post = postResponse.data;

          // Get fresh comments
          const commentsResponse = await api.get(`/posts/${postId}/comments`, {
            params: { page: 1, limit: COMMENTS_PER_PAGE }
          });

          // Update post with fresh data
          set((state) => {
            const updatedPosts = state.posts.map((p) =>
              p.id === postId
                ? {
                  ...post,
                  commentList: commentsResponse.data.comments
                }
                : p
            );
            
            return {
              posts: updatedPosts,
              currentPost: state.currentPost?.id === postId
                ? {
                  ...state.currentPost,
                  commentList: commentsResponse.data.comments
                }
                : state.currentPost
            };
          });
        } catch (error) {
          set({ error: 'Error deleting comment' });
          throw error;
        } finally {
          set({ isDeletingComment: false });
        }
      },

      getComments: async (postId: string, page: number = 1, limit?: number) => {
        try {
          const response = await api.get(`/posts/${postId}/comments`, {
            params: { page, limit: limit || COMMENTS_PER_PAGE }
          });

          // If it's page 1, replace comments, otherwise append
          set((state) => {
            const getUpdatedComments = (currentComments: Comment[] | undefined) =>
              page === 1
                ? response.data.comments
                : [...(currentComments || []), ...response.data.comments];

            const updatedPosts = state.posts.map((post) =>
              post.id === postId
                ? {
                  ...post,
                  commentList: getUpdatedComments(post.commentList)
                }
                : post
            );
            
            return {
              posts: updatedPosts,
              currentPost: state.currentPost?.id === postId
                ? {
                  ...state.currentPost,
                  commentList: getUpdatedComments(state.currentPost.commentList)
                }
                : state.currentPost
            };
          });

          return response.data;
        } catch (error) {
          console.error('Error getting comments:', error);
          throw error;
        }
      },

      // Share post
      sharePost: async (postId: string, content?: string) => {
        try {
          await api.post(`/posts/${postId}/share`, { content });
          get().fetchPosts(1); // Refresh feed
        } catch (error) {
          console.error('Error sharing post:', error);
          throw error;
        }
      },

      // Store Actions
      setLoading: (loading: boolean) => set({ loading }),
      setError: (error: string | null) => set({ error }),
      reset: () => {
        set({
          posts: [],
          loading: false,
          error: null,
          currentPage: 1,
          hasNextPage: false,
          total: 0,
        });
      },
    }),
    { name: 'post-store' }
  )
);
